/**
 * API Client for CCM Backend
 */

import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { logger } from '../../utils/logger';

export interface APIResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: string[];
}

// Module-level flag: once idle timeout is detected, all subsequent requests
// return never-resolving promises immediately (no network call).
// The page reload after signOut resets this naturally.
let signingOutDueToIdleTimeout = false;

// Module-level flag: once Cloud Armor blocking is detected mid-session
// (e.g. temp access expired), sign out and reload to land on login page.
let signingOutDueToCloudArmor = false;

export class APIClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // Use provided URL, or env variable, or default to localhost
    this.baseUrl = baseUrl || import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    logger.log('[APIClient] Using API base URL:', this.baseUrl);
  }

  /**
   * Get Firebase ID token for authentication
   */
  private async getAuthToken(forceRefresh: boolean = false): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      // Force refresh token to get a fresh one with current timestamp
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      logger.error('Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<APIResponse<T>> {
    // Already signing out — don't make any more API calls
    if (signingOutDueToIdleTimeout || signingOutDueToCloudArmor) {
      return new Promise<APIResponse<T>>(() => {});
    }

    const token = await this.getAuthToken(retryCount > 0);

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Only add Content-Type for requests with a body (POST, PUT, PATCH)
    if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (fetchError) {
      // Network error (TypeError) — could be Cloud Armor blocking (no CORS headers)
      // or a genuine network failure.  Check the CA-exempt access-check endpoint.
      if (!signingOutDueToCloudArmor) {
        try {
          const check = await fetch(`${this.baseUrl}/api/v1/access-check`);
          if (check.ok) {
            // Backend is up but our request was blocked → Cloud Armor is blocking this IP
            signingOutDueToCloudArmor = true;
            logger.log('[APIClient] Cloud Armor blocking detected, signing out');
            signOut(auth)
              .catch(() => {})
              .finally(() => { window.location.reload(); });
            return new Promise<APIResponse<T>>(() => {});
          }
        } catch {
          // access-check also failed → backend is down, not Cloud Armor
        }
      }
      throw fetchError;
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      const errorData = await response.json().catch(() => null);

      // Server-side idle timeout — sign out and force a full page reload.
      // The reload kills all in-flight requests, retry loops, and module state.
      // After signOut clears the persisted auth, the reload lands on the login page.
      if (errorData?.error_code === 'SESSION_IDLE_TIMEOUT') {
        if (!signingOutDueToIdleTimeout) {
          signingOutDueToIdleTimeout = true;
          logger.log('[APIClient] Server-side idle timeout detected, signing out');
          signOut(auth)
            .catch(() => {})
            .finally(() => { window.location.reload(); });
        }
        return new Promise<APIResponse<T>>(() => {});
      }

      // Clock skew retry (once only)
      if (retryCount === 0) {
        if (errorData?.message?.includes('Token used too early') ||
            errorData?.message?.includes('Invalid token')) {
          logger.log('Token timing issue detected, refreshing token and retrying...');
          return this.request<T>(endpoint, options, 1);
        }
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`
      }));
      const detail = errorData.detail;
      // When detail is a structured object (e.g. transition block errors),
      // use its message string; otherwise use the raw detail string.
      const msg = (typeof detail === 'object' && detail?.message)
        ? detail.message
        : (typeof detail === 'string' ? detail : null)
          || errorData.message
          || `Request failed: ${response.status}`;
      const error: any = new Error(msg);
      // Attach structured detail and status for callers that need it (KAN-199)
      error.detail = detail;
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData<T>(endpoint: string, formData: FormData): Promise<APIResponse<T>> {
    const token = await this.getAuthToken();
    
    const headers: Record<string, string> = {};

    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Don't set Content-Type for FormData - browser will set it with boundary

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.detail || errorData.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request with FormData (for file uploads)
   */
  async putFormData<T>(endpoint: string, formData: FormData): Promise<APIResponse<T>> {
    const token = await this.getAuthToken();
    
    const headers: Record<string, string> = {};

    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Don't set Content-Type for FormData - browser will set it with boundary

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.detail || errorData.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Health check endpoint (no auth required)
   */
  async healthCheck(): Promise<APIResponse<any>> {
    const response = await fetch(`${this.baseUrl}/health`);
    return response.json();
  }

  /**
   * Access check endpoint (no auth required)
   * Used to verify if user's IP is whitelisted before showing login
   */
  async accessCheck(): Promise<APIResponse<any>> {
    const response = await fetch(`${this.baseUrl}/api/v1/access-check`);
    if (!response.ok) {
      throw new Error(`Access denied: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Whitelist check endpoint (no auth required, but subject to Cloud Armor)
   * Returns 200 if IP is whitelisted, 403 if blocked by Cloud Armor
   */
  async whitelistCheck(): Promise<APIResponse<any>> {
    const response = await fetch(`${this.baseUrl}/api/v1/whitelist-check`);
    if (!response.ok) {
      throw new Error(`IP not whitelisted: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Request temporary IP whitelisting (KAN-204).
   * Accepts the Firebase ID token explicitly — the user authenticates
   * specifically for this flow and isn't yet in a full app session.
   */
  async requestTempAccess(idToken: string, durationHours: number): Promise<APIResponse<any>> {
    const response = await fetch(`${this.baseUrl}/api/v1/request-temp-access`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ durationHours }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `Request failed: ${response.status}`,
      }));
      throw new Error(errorData.detail || errorData.message || `Request failed: ${response.status}`);
    }
    return response.json();
  }
}

// Export singleton instance
export const apiClient = new APIClient();